import React, { useState, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import './UploadModal.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTimes,
  faCloudArrowUp,
  faSearch,
  faCheck,
  faChevronDown,
  faChevronUp,
  faArrowLeft
} from '@fortawesome/free-solid-svg-icons';
import BulkUpload from './BulkUpload';
import { useUser } from '../../UserContext';

const CHUNK_SIZE =  55 * 1024 * 1024; // Match BulkUpload chunk size

function UploadModal({ isOpen, onClose, onUploadStatusChange }) {
  const { user } = useUser();
  const [parentProjects, setParentProjects] = useState([]);
  const [selectedParentId, setSelectedParentId] = useState(null);
  const [expandedParentId, setExpandedParentId] = useState(null);
  const [subprojects, setSubprojects] = useState({});
  const [selectedSubprojectId, setSelectedSubprojectId] = useState(null);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [selectedAnalysis, setSelectedAnalysis] = useState('');
  const [selectedSample, setSelectedSample] = useState('');
  const [selectedFiles, setSelectedFiles] = useState({ read1: null, read2: null });
  const [patients, setPatients] = useState([]);
  const [patientSearch, setPatientSearch] = useState('');
  const [analysisTypes, setAnalysisTypes] = useState({});
  const [sampleTypes, setSampleTypes] = useState({});
  const [supportedSamples, setSupportedSamples] = useState([]);
  const [uploadProgress, setUploadProgress] = useState({ read1: 0, read2: 0 });
  const [isUploading, setIsUploading] = useState(false);
  const [currentFileName, setCurrentFileName] = useState('');
  const [newFileName, setNewFileName] = useState('');
  const [isBulkMode, setIsBulkMode] = useState(false);
  const [isBulkUploading, setIsBulkUploading] = useState(false);
  
  // Create a ref to store AbortController for upload cancellation
  const uploadControllerRef = useRef(null);

  const resetSelections = () => {
    setSelectedParentId(null);
    setSelectedSubprojectId(null);
    setSelectedPatient(null);
    setSelectedAnalysis('');
    setSelectedSample('');
    setSelectedFiles({ read1: null, read2: null });
    setUploadProgress({ read1: 0, read2: 0 });
    setPatientSearch('');
    setExpandedParentId(null);
  };

  useEffect(() => {
    if (isOpen) {
      const fetchParentProjects = async () => {
        try {
          const response = await fetch('https://service9.szapfs.org/parent-projects');
          if (!response.ok) throw new Error('Network response was not ok');
          const data = await response.json();
          setParentProjects(data.sort((a, b) => a.id - b.id));
        } catch (error) {
          console.error('Error fetching parent projects:', error);
        }
      };
      fetchParentProjects();
    } else {
      resetSelections();
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      const fetchAnalysisTypes = async () => {
        try {
          const response = await fetch('https://test-tunnel-b1d9.szapfs.org/supported-analyses');
          if (!response.ok) throw new Error('Network response was not ok');
          const data = await response.json();
          setAnalysisTypes(data.analyses || {});
          setSampleTypes(data.sampleTypes || {});
        } catch (error) {
          console.error('Error fetching analysis types:', error);
        }
      };
      fetchAnalysisTypes();
    }
  }, [isOpen]);

  useEffect(() => {
    if (expandedParentId) {
      const fetchSubprojects = async () => {
        try {
          const response = await fetch(
            `https://service9.szapfs.org/parent-project/${expandedParentId}/subprojects`
          );
          if (!response.ok) throw new Error('Network response was not ok');
          const data = await response.json();
          setSubprojects(prev => ({ ...prev, [expandedParentId]: data }));
        } catch (error) {
          console.error('Error fetching subprojects:', error);
        }
      };
      fetchSubprojects();
    }
  }, [expandedParentId]);

  useEffect(() => {
    if (selectedParentId && selectedSubprojectId) {
      const fetchPatients = async () => {
        try {
          const response = await fetch(
            `https://service9.szapfs.org/project/${selectedParentId}/${selectedSubprojectId}/patients`
          );
          if (!response.ok) throw new Error('Network response was not ok');
          const data = await response.json();
          const sortedData = data.sort((a, b) => {
            const aNum = parseInt(a.patient_id.split('_')[2]) || 0;
            const bNum = parseInt(b.patient_id.split('_')[2]) || 0;
            return aNum - bNum;
          });
          setPatients(sortedData);
        } catch (error) {
          console.error('Error fetching patients:', error);
        }
      };
      fetchPatients();
    } else {
      setPatients([]);
    }
    setSelectedPatient(null);
    setSelectedAnalysis('');
    setSelectedSample('');
    setSelectedFiles({ read1: null, read2: null });
  }, [selectedParentId, selectedSubprojectId]);

  useEffect(() => {
    if (selectedAnalysis && analysisTypes[selectedAnalysis]) {
      setSupportedSamples(analysisTypes[selectedAnalysis].supportedSamples || []);
      setSelectedSample('');
      setSelectedFiles({ read1: null, read2: null });
    } else {
      setSupportedSamples([]);
    }
  }, [selectedAnalysis, analysisTypes]);

  useEffect(() => {
    // Effect to notify parent about uploading state
    if (onUploadStatusChange) {
      onUploadStatusChange(isUploading);
    }
  }, [isUploading, onUploadStatusChange]);

  const handleParentClick = parentId => {
    setSelectedParentId(parentId);
    setExpandedParentId(expandedParentId === parentId ? null : parentId);
    if (expandedParentId !== parentId) {
      setSelectedSubprojectId(null);
    }
    // Clear file selections on parent project change
    setSelectedFiles({ read1: null, read2: null });
  };

  const handleSubprojectClick = subprojectId => {
    setSelectedSubprojectId(subprojectId);
    // Clear file selections on subproject change
    setSelectedFiles({ read1: null, read2: null });
  };

  // Utility function to determine read type from filename - similar to BulkUpload validation logic
  const detectReadType = (fileName) => {
    if (!fileName) return null;
    
    const lowerName = fileName.toLowerCase();
    
    // Check for standard R1/R2 patterns (most common)
    if (lowerName.includes('_r1') || lowerName.includes('-r1') || lowerName.includes('.r1')) return 'read1';
    if (lowerName.includes('_r2') || lowerName.includes('-r2') || lowerName.includes('.r2')) return 'read2';
    
    // Check for patterns with just r1/r2 in name
    if (lowerName.includes('r1')) return 'read1';
    if (lowerName.includes('r2')) return 'read2';
    
    // Also check for _1/_2 pattern (common in FASTQ)
    if (lowerName.includes('_1.') || lowerName.match(/_1$/)) return 'read1';
    if (lowerName.includes('_2.') || lowerName.match(/_2$/)) return 'read2';
    
    return null;
  };

  const handleFileSelect = (event, fileType) => {
    const file = event.target.files[0];
    if (!file) return;
    
    // Validate file based on analysis type (FASTQ vs non-FASTQ)
    if (isFastq) {
      // For FASTQ, check the read type
      const detectedType = detectReadType(file.name);
      
      if (!detectedType) {
        alert('FASTQ file names should include R1/R2 or _1/_2 to indicate read type');
        return;
      }
      
      // Auto-correct file placement if needed
      if (detectedType !== fileType) {
        fileType = detectedType;
      }
    }
    
    setSelectedFiles(prev => ({ ...prev, [fileType]: file }));
  };

  // Determine if FASTQ (paired reads) or single file is needed based on analysis type
  const analysisDetails = analysisTypes[selectedAnalysis] || {};
  const isFastq = analysisDetails.uploadType === 'FASTQ';

  // Helper: upload single chunk - matches BulkUpload implementation
  const uploadChunk = async (file, chunkIndex, uploadKey, controller) => {
    const startByte = chunkIndex * CHUNK_SIZE;
    const endByte = Math.min(startByte + CHUNK_SIZE, file.size);
    const chunk = file.slice(startByte, endByte);

    const formData = new FormData();
    // These must come before file
    formData.append('uploadKey', uploadKey);
    formData.append('chunkIndex', String(chunkIndex));
    // Then the file
    formData.append('chunk', chunk);

    const res = await fetch('https://test-tunnel-b1d9.szapfs.org/upload-chunk-simple', {
      method: 'POST',
      body: formData,
      signal: controller.signal
    });
    if (!res.ok) throw new Error(`Failed to upload chunkIndex=${chunkIndex}`);
    return res.json();
  };

  const handleUpload = async () => {
    // Validate files based on analysis type
    if (isFastq && (!selectedFiles.read1 || !selectedFiles.read2)) {
      alert('FASTQ analysis requires both Read 1 (R1) and Read 2 (R2) files');
      return;
    }
    if (!isFastq && !selectedFiles.read1) {
      alert('Please select a file to upload');
      return;
    }
    
    // Additional validation for FASTQ files
    if (isFastq) {
      const read1Type = detectReadType(selectedFiles.read1.name);
      const read2Type = detectReadType(selectedFiles.read2.name);
      
      if (read1Type !== 'read1' || read2Type !== 'read2') {
        // Try to auto-correct by swapping
        if (read1Type === 'read2' && read2Type === 'read1') {
          const temp = selectedFiles.read1;
          selectedFiles.read1 = selectedFiles.read2;
          selectedFiles.read2 = temp;
        } else {
          alert('Please ensure your Read 1 file is properly named with R1 and Read 2 file with R2');
          return;
        }
      }
    }
    
    setIsUploading(true);
    
    // Create a new AbortController for this upload
    const controller = new AbortController();
    uploadControllerRef.current = controller;

    // Generate random upload keys
    const uploadKeyR1 = `${Date.now()}_${Math.random().toString(36).substring(7)}_R1`;
    const uploadKeyR2 = isFastq && selectedFiles.read2 ? `${Date.now()}_${Math.random().toString(36).substring(7)}_R2` : null;

    // Setup metadata
    const metadata = {
      analysisCode: selectedAnalysis,
      sampleCode: selectedSample,
      patientId: selectedPatient.patient_id,
      subprojectId: selectedSubprojectId,
      originalFileName: selectedFiles.read1.name + (isFastq && selectedFiles.read2 ? ',' + selectedFiles.read2.name : '')
    };

    try {
      // Upload read1
      const uploadRead1 = async () => {
        const file = selectedFiles.read1;
        setCurrentFileName(file.name);
        const total1 = Math.ceil(file.size / CHUNK_SIZE);
        
        for (let i = 0; i < total1; i++) {
          await uploadChunk(file, i, uploadKeyR1, controller);
          setUploadProgress(prev => ({
            ...prev,
            read1: ((i + 1) / total1) * 100
          }));
        }
      };

      // Upload read2 if FASTQ
      const uploadRead2 = async () => {
        if (!isFastq || !selectedFiles.read2) return;
        
        const file = selectedFiles.read2;
        const total2 = Math.ceil(file.size / CHUNK_SIZE);
        
        for (let i = 0; i <total2; i++) {
          await uploadChunk(file, i, uploadKeyR2, controller);
          setUploadProgress(prev => ({
            ...prev,
            read2: ((i + 1) / total2) * 100
          }));
        }
      };

      // Run uploads
      if (isFastq && selectedFiles.read2) {
        await Promise.all([uploadRead1(), uploadRead2()]);
      } else {
        await uploadRead1();
      }

      // Finalize upload
      const totalChunksR1 = Math.ceil(selectedFiles.read1.size / CHUNK_SIZE);
      let maxChunks = totalChunksR1;
      
      if (isFastq && selectedFiles.read2) {
        const totalChunksR2 = Math.ceil(selectedFiles.read2.size / CHUNK_SIZE);
        maxChunks = Math.max(maxChunks, totalChunksR2);
      }
      
      const finalizeBody = {
        uploadKeyR1: uploadKeyR1,
        uploadKeyR2: uploadKeyR2,
        totalChunks: maxChunks,
        metadata
      };
      
      const finRes = await fetch('https://test-tunnel-b1d9.szapfs.org/finalize-upload-simple', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(finalizeBody)
      });
      
      if (!finRes.ok) throw new Error('Finalization failed');
      await finRes.json();

      // Success - reset form after delay
      setTimeout(() => {
        setIsUploading(false);
        setUploadProgress({ read1: 0, read2: 0 });
        setCurrentFileName('');
        setNewFileName('');
        setSelectedFiles({ read1: null, read2: null });
        resetSelections();
        onClose();
      }, 2000);
    } catch (err) {
      if (err.name === 'AbortError') {
        console.log('Upload was canceled');
      } else {
        console.error('Upload error:', err);
      }
      
      setIsUploading(false);
      setUploadProgress({ read1: 0, read2: 0 });
      setCurrentFileName('');
      setNewFileName('');
    } finally {
      uploadControllerRef.current = null;
    }
  };

  // Cancel upload
  const cancelUpload = () => {
    if (uploadControllerRef.current) {
      uploadControllerRef.current.abort();
      uploadControllerRef.current = null;
      setIsUploading(false);
      setUploadProgress({ read1: 0, read2: 0 });
    }
  };

  // Cleanup function to cancel upload if component unmounts
  useEffect(() => {
    return () => {
      if (uploadControllerRef.current) {
        uploadControllerRef.current.abort();
        console.log('UploadModal component unmounted, upload aborted');
      }
    };
  }, []);

  const filteredPatients = useMemo(() => {
    if (!patientSearch) return patients;
    const searchLower = patientSearch.toLowerCase();
    return patients.filter(
      patient =>
        (patient.name?.toLowerCase().includes(searchLower) ||
         patient.patient_id?.toLowerCase().includes(searchLower))
    );
  }, [patients, patientSearch]);

  const handleBulkUploadStatus = (isUploading) => {
    setIsBulkUploading(isUploading);
  };

  if (!isOpen) return null;

  // Create portal content to render directly to document body

  const modalContent = (
    <div className="UPM-modal-overlay">
      <div 
        className={`UPM-modal ${isBulkMode ? 'bulk-mode' : ''} ${isUploading ? 'uploading' : ''}`}
      >
        <div className="UPM-modal-header">
          <h2>
            {isBulkMode ? (
              <button 
                className="header-back-button" 
                onClick={() => setIsBulkMode(false)}
                disabled={isBulkUploading}
                style={{ opacity: isBulkUploading ? 0.5 : 1 }}
              >
                <FontAwesomeIcon icon={faArrowLeft} /> Back
              </button>
            ) : (
              'Upload Data'
            )}
          </h2>
          {!isUploading && (
            <button 
              className="UPM-close-button" 
              onClick={onClose}
              disabled={isBulkUploading}
              style={{ opacity: isBulkUploading ? 0.5 : 1 }}
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          )}
        </div>
        <div className="UPM-modal-content">
          {isBulkMode ? (
            <BulkUpload onUploadStatusChange={handleBulkUploadStatus} />
          ) : (
            <>
              {isUploading ? (
                <div className="UPM-upload-progress">
                  <h3>Uploading {currentFileName}</h3>
                  {isFastq ? (
                    <div className='progress-container'>
                      <div className="progress-container">
                        <label>Read 1:</label>
                        <div className="progress-bar">
                          <div
                            className="progress"
                            style={{
                              width: `${uploadProgress.read1}%`,
                              background: 'linear-gradient(170deg, #2595be7e 0%, #20558aec 100%)'
                            }}
                          />
                        </div>
                      </div>
                      <div className="progress-container">
                        <label>Read 2:</label>
                        <div className="progress-bar">
                          <div
                            className="progress"
                            style={{
                              width: `${uploadProgress.read2}%`,
                              background: 'linear-gradient(170deg, #2595be7e 0%, #20558aec 100%)'
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="progress-bar">
                      <div
                        className="progress"
                        style={{
                          width: `${uploadProgress.read1}%`,
                          background: 'linear-gradient(170deg, #2595be7e 0%, #20558aec 100%)'
                        }}
                      />
                    </div>
                  )}
                  <button className="cancel-upload-button" onClick={cancelUpload}>Cancel Upload</button>
                </div>
              ) : (
                <>
                  <div className="UPM-selection-grid">
                    <div className="UPM-form-section">
                      <h3>Project</h3>
                      <div className="UPM-selection-box">
                        <div className="UPM-selection-list">
                          {parentProjects
                            .filter(parent => 
                              user?.role === 'Reviewer' ? parent.id === 5 : true
                            )
                            .map(parent => (
                            <div key={parent.id}>
                              <div
                                className={`UPM-selection-item ${selectedParentId === parent.id ? 'selected' : ''
                                }`}
                                onClick={() => handleParentClick(parent.id)}
                              >
                                <span className="UPM-item-name">{parent.name}</span>
                                <FontAwesomeIcon
                                  icon={expandedParentId === parent.id ? faChevronUp : faChevronDown}
                                  className="UPM-expand-icon"
                                />
                              </div>
                              {expandedParentId === parent.id && subprojects[parent.id] && (
                                <div className="UPM-subprojects-list">
                                  {subprojects[parent.id].map(sub => (
                                    <div
                                      key={sub.project_id}
                                      className={`UPM-subproject-item ${
                                        selectedSubprojectId === sub.project_id ? 'selected' : ''
                                      }`}
                                      onClick={() => handleSubprojectClick(sub.project_id)}
                                    >
                                      <span className="UPM-item-name">Project {sub.project_id}</span>
                                      <span className="UPM-item-id">{sub.description}</span>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>

                    <div className="UPM-form-section">
                      <h3>Patient</h3>
                      <div className="UPM-selection-box">
                        <div className="FUPM-search-box">
                          <input
                            type="text"
                            placeholder="Search patients..."
                            value={patientSearch}
                            onChange={e => setPatientSearch(e.target.value)}
                            disabled={!selectedSubprojectId}
                          />
                        </div>
                        <div className="UPM-selection-list">
                          {filteredPatients.map(patient => (
                            <div
                              key={patient.patient_id}
                              className={`UPM-selection-item ${
                                selectedPatient?.patient_id === patient.patient_id ? 'selected' : ''
                              }`}
                              onClick={() => setSelectedPatient(patient)}
                            >
                              <span className="UPM-item-name">{patient.name || 'No Name'}</span>
                              <span className="UPM-item-id">{patient.patient_id}</span>
                              {selectedPatient?.patient_id === patient.patient_id && (
                                <FontAwesomeIcon icon={faCheck} className="UPM-check-icon" />
                              )}
                            </div>
                          ))}
                          {selectedSubprojectId && filteredPatients.length === 0 && (
                            <div className="UPM-no-results">No patients found</div>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="UPM-form-section">
                      <h3>Analysis Type</h3>
                      <select
                        value={selectedAnalysis}
                        onChange={e => setSelectedAnalysis(e.target.value)}
                        disabled={!selectedPatient}
                      >
                        <option value="">Select Analysis Type</option>
                        {Object.entries(analysisTypes).map(([code, details]) => (
                          <option key={code} value={code}>
                            {details.name} ({code})
                          </option>
                        ))}
                      </select>
  
                    </div>

                    <div className="UPM-form-section">
                      <h3>Sample Type</h3>
                      <select
                        value={selectedSample}
                        onChange={e => setSelectedSample(e.target.value)}
                        disabled={!selectedAnalysis || supportedSamples.length === 0}
                      >
                        <option value="">Select Sample Type</option>
                        {supportedSamples.map(code => (
                          <option key={code} value={code}>
                            {sampleTypes[code]?.name} ({code})
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="UPM-file-section">
                      <h3>Upload Files</h3>
                      {isFastq ? (
                        <div className="UPM-dual-file-upload">
                          <div className="UPM-file-input">
                            <label className={`UPM-file-input-label ${!selectedSample ? 'disabled' : ''}`}>
                              <input
                                type="file"
                                onChange={e => handleFileSelect(e, 'read1')}
                                accept=".gz"
                                disabled={!selectedSample}
                                style={{ display: 'none' }}
                              />
                              <div className="UPM-file-input-content">
                                <FontAwesomeIcon icon={faCloudArrowUp} className="UPM-file-icon" />
                                <span>Select Read 1 FASTQ</span>
                              </div>
                            </label>
                            {selectedFiles.read1 && (
                              <div className="UPM-selected-file">Selected: {selectedFiles.read1.name}</div>
                            )}
                          </div>
                          <div className="UPM-file-input">
                            <label className={`UPM-file-input-label ${!selectedSample ? 'disabled' : ''}`}>
                              <input
                                type="file"
                                onChange={e => handleFileSelect(e, 'read2')}
                                accept=".gz"
                                disabled={!selectedSample}
                                style={{ display: 'none' }}
                              />
                              <div className="UPM-file-input-content">
                                <FontAwesomeIcon icon={faCloudArrowUp} className="UPM-file-icon" />
                                <span>Select Read 2 FASTQ</span>
                              </div>
                            </label>
                            {selectedFiles.read2 && (
                              <div className="UPM-selected-file">Selected: {selectedFiles.read2.name}</div>
                            )}
                          </div>
                        </div>
                      ) : (
                        <div className="UPM-file-input">
                          <label className={`UPM-file-input-label ${!selectedSample ? 'disabled' : ''}`}>
                            <input
                              type="file"
                              onChange={e => handleFileSelect(e, 'read1')}
                              disabled={!selectedSample}
                              style={{ display: 'none' }}
                            />
                            <div className="UPM-file-input-content">
                              <FontAwesomeIcon icon={faCloudArrowUp} className="UPM-file-icon" />
                              <span>Select Single File</span>
                            </div>
                          </label>
                          {selectedFiles.read1 && (
                            <div className="UPM-selected-file">Selected: {selectedFiles.read1.name}</div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="UPM-modal-footer">
                    {user?.role !== 'Reviewer' && (
                      <button className="bulk-upload-btn" onClick={() => setIsBulkMode(true)}>
                        Bulk Upload
                      </button>
                    )}
                    {!isUploading && (
                      <button
                        className="UPM-upload-button"
                        onClick={handleUpload}
                        disabled={
                          !selectedParentId ||
                          !selectedSubprojectId ||
                          !selectedPatient ||
                          !selectedAnalysis ||
                          !selectedSample ||
                          !selectedFiles.read1 ||
                          (isFastq && !selectedFiles.read2)
                        }
                      >
                        <FontAwesomeIcon icon={faCloudArrowUp} /> Upload
                      </button>
                    )}
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
  
  // Use createPortal to render modal directly to document body
  return createPortal(modalContent, document.body);
}

export default UploadModal;
